// The client-side move processor as a pure state machine. State is
// SELF-CONTAINED: everything a transition needs — including single-step
// undo — lives in the state itself, so a machine built from a server
// snapshot behaves identically to one that watched every move live.
//
// State shape: { board, prevBoard } where prevBoard is the board before
// the most recent move, or null. This shape encodes the single-step-undo
// rule directly: after an undo the prevBoard slot is empty, so a second
// undo is unrepresentable rather than merely refused.
//
// Pure module: no React, no DOM, no network. The same machine drives
// spectated games (fed by wire ops) and could drive replays.

import { applyMove, legalMoves, replay } from "./solitaire.js";

// Build a machine from a server snapshot: the game's move history plus
// whether undo is currently armed (the server's lastWasUndo, inverted).
// prevBoard is derived by replaying one move short — the snapshot is
// self-contained, so a just-connected spectator can honor an undo that
// was minted before they arrived.
export function machineFromSnapshot(seed, moves, canUndo, opts = {}) {
  const board = replay(seed, moves, opts);
  const prevBoard =
    canUndo && moves.length > 0 ? replay(seed, moves.slice(0, -1), opts) : null;
  return { board, prevBoard };
}

// Apply one wire move (an index into legalMoves(board)). Returns
// { machine, move } on success, or null if the index doesn't name a
// legal move — which after the seq-guarded protocol means a genuine
// protocol violation, never a silently reinterpreted move.
export function machineApply(machine, moveIndex) {
  const mv = legalMoves(machine.board)[moveIndex];
  if (!mv) return null;
  return {
    machine: { board: applyMove(machine.board, mv), prevBoard: machine.board },
    move: mv,
  };
}

// Take back the most recent move. Returns the new machine, or null when
// undo isn't armed (nothing applied yet, or the last transition was
// itself an undo).
export function machineUndo(machine) {
  if (!machine.prevBoard) return null;
  return { board: machine.prevBoard, prevBoard: null };
}
